import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  updateFormField,
  togglePasswordVisibility,
  handlePopup,
} from "./redux/userSlice";
import { createUser, fetchOrgUsersById } from "./redux/userThunk";
import userStore from "../../zustand/userStore";
import LoaderOverlay from "../LoaderOverlay";

// shared field styles — keeps every input the same width/height
const gridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  columnGap: "32px",
  rowGap: "16px",
};

const labelStyle = {
  display: "block",
  textAlign: "left",
  marginBottom: "4px",
  fontSize: "14px",
  fontWeight: 500,
};

const inputStyle = {
  width: "100%",
  height: "38px",
  boxSizing: "border-box",
};

const eyeIconStyle = {
  position: "absolute",
  right: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  cursor: "pointer",
  color: "#6c757d",
};

const CreateUser = () => {
  const dispatch = useDispatch();
  const { formData, status, error, showPassword, showConfirmPassword } =
    useSelector((state) => state.user);

  const userData = userStore((state) => state.user);

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateFormField({ field: name, value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }

    if (!/^\d{10}$/.test(formData.contact_number)) {
      alert("Contact number must be exactly 10 digits");
      return;
    }

    const body = {
      username: formData.username,
      email: formData.email,
      password: formData.password,
      role_id: 1,
      created_by: userData.id,
      school_name: userData.school_name,
      contact_number: formData.contact_number,
      address: userData.address,
      school_type: "Primary",
      db_name: userData.db_name,
      port: userData.port,
      account_type: userData.account_type,
      parent_id: userData.admin_id || "",
    };

    try {
      await dispatch(createUser(body)).unwrap();
      alert("User created successfully");
      dispatch(fetchOrgUsersById(userData?.id));
      dispatch(handlePopup(false));
    } catch (err) {
      console.error(err);
      alert(err || "Failed to create user");
    }
  };

  return (
    <>
      <AnimatePresence>
        {status === "loading" && <LoaderOverlay />}
      </AnimatePresence>

      <motion.div
        style={{ marginTop: "4px" }}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div style={{ maxWidth: "680px", margin: "0 auto" }}>
          <form onSubmit={handleSubmit}>
            <div style={gridStyle}>
              {/* Username */}
              <div>
                <label style={labelStyle}>Username</label>
                <input
                  type="text"
                  name="username"
                  className="form-control"
                  style={inputStyle}
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  style={inputStyle}
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Password */}
              <div>
                <label style={labelStyle}>Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="form-control"
                    style={{ ...inputStyle, paddingRight: "38px" }}
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <i
                    className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"
                      }`}
                    style={eyeIconStyle}
                    onClick={() =>
                      dispatch(togglePasswordVisibility("showPassword"))
                    }
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label style={labelStyle}>Confirm Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    className="form-control"
                    style={{ ...inputStyle, paddingRight: "38px" }}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                  <i
                    className={`fa-solid ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                      }`}
                    style={eyeIconStyle}
                    onClick={() =>
                      dispatch(togglePasswordVisibility("showConfirmPassword"))
                    }
                  />
                </div>
              </div>

              {/* Role (Commented Out) */}
              {/*
              <div>
                <label style={labelStyle}>Role</label>
                <select
                  name="role_id"
                  className="form-select"
                  style={inputStyle}
                  value={formData.role_id}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select Role
                  </option>
                  <option value={2}>Admin</option>
                  <option value={1}>User</option>
                </select>
              </div>
              */}

              {/* Contact Number */}
              <div>
                <label style={labelStyle}>Contact Number</label>
                <input
                  type="tel"
                  className="form-control"
                  style={inputStyle}
                  value={formData.contact_number || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) {
                      dispatch(
                        updateFormField({
                          field: "contact_number",
                          value,
                        })
                      );
                    }
                  }}
                  maxLength={10}
                  required
                />
              </div>
            </div>

            <div
              style={{
                textAlign: "center",
                marginTop: "20px",
                marginBottom: "8px",
              }}
            >
              <button type="submit" className="btn btn-primary px-5">
                Submit
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </>
  );
};

export default CreateUser;
